import { useEffect, useMemo, useRef, useState } from 'react';
import ConfirmModal from './ConfirmModal';
import AmberCallout from './AmberCallout';
import Icon from './Icon';
import { toast } from './Toast';
import { useAuth } from '../context/AuthContext';
import { documentsApi } from '../utils/api';
import { track, EVENTS } from '../utils/analytics';

// Move a document to another restaurant (#304, server/lib/documentMove.js) — the
// way out for one filed under the wrong customer, instead of deleting it and
// uploading it again there.
//
// An approved document moves too (#361) and arrives as a draft, to be approved
// there. There is no next draft to go on to, so the button is just העבר, and
// the callout says what the move cannot take back (the server's `notices`).
//
// The picker IS the confirmation: one restaurant chosen, one button. When the
// recipient points at exactly one other restaurant it arrives pre-selected, so
// the common case is two taps (the warning chip, then העבר והבא). Several
// branches → all listed, none chosen. Below them, the same 3-character search
// as the tenant chip, over every restaurant.
//
// Under the CHOSEN restaurant only, one line from the server's preview: quiet
// when the supplier exists there, a note when the draft will arrive without
// one, or the blocker that stops the move (the button stays disabled).
//
// `review` builds the page's timing payload, as DeleteDocumentDialog's does.
// `unsavedEdits` / `pendingNote` say what the move leaves behind: it takes the
// SAVED document, and a note waiting for approval is never sent.
// `onMoved({ notice, doneLead, moved })`: the page shows the toast once it
// knows where it lands (the next draft, or the queue's end).
//
// After the design review (31/10/2026): a draft that can move NOWHERE
// (payments, decided before, links, …) opens on the reason alone, with one
// סגור; the confident suggestion (doc.move_suggestion) renders at once and
// its plan arrives with the first answer, and nothing reflows while a
// restaurant is being checked — the confirm just waits.
const MIN_QUERY = 3;
const GROUP_TITLE = { bid: 'הצעה לפי ח.פ. הנמען', name: 'הצעה לפי שם הנמען' };

/** "ל" / "ב" joined to a Hebrew name; a hyphen before a Latin or digit start ("ל-Pizza"). */
export const joined = (name) => (/^[֐-׿]/.test(name || '') ? name : `-${name}`);

// What the move did to this restaurant's delivery notes (#361), for the toast.
export const repricedLine = ({ notes_repriced: notes = 0, lines_kept: kept = 0, notes_to_check: toCheck = 0 }) => [
  notes === 1 && 'תעודת משלוח אחת תומחרה מחדש',
  notes > 1 && `${notes} תעודות משלוח תומחרו מחדש`,
  kept === 1 && 'שורה אחת נשארה במחיר הישן כמשוער',
  kept > 1 && `${kept} שורות נשארו במחיר הישן כמשוער`,
  toCheck === 1 && 'תעודה אחת לבדיקה ידנית',
  toCheck > 1 && `${toCheck} תעודות לבדיקה ידנית`,
].filter(Boolean).map(part => ` · ${part}`).join('');

// "(11, 12, …)": the numbers the server named, and whether there are more.
const numbered = ({ count, doc_nums: nums = [] }) => (nums.length
  ? <> <bdi>({nums.join(', ')}{count > nums.length ? ', …' : ''})</bdi></>
  : null);

export default function MoveDocumentDialog({ doc, review = null, unsavedEdits = false, pendingNote = false, onMoved, onCancel }) {
  const { restaurants = [] } = useAuth();
  const seed = doc.move_suggestion?.confident && doc.move_suggestion.restaurant_id != null
    ? { restaurant_id: doc.move_suggestion.restaurant_id, name: doc.move_suggestion.name, matched_by: doc.move_suggestion.matched_by }
    : null;
  const [suggestions, setSuggestions] = useState(seed ? [seed] : null);   // null = loading
  const [docBlockers, setDocBlockers] = useState([]);   // the move is impossible to any restaurant
  const [notices, setNotices] = useState(null);         // what it leaves behind, whatever the restaurant
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState(seed?.restaurant_id ?? null);
  const [query, setQuery] = useState('');
  const [plan, setPlan] = useState(null);                 // the preview of one restaurant (plan.target.id)
  const [busy, setBusy] = useState(false);
  const planSeq = useRef(0);

  useEffect(() => {
    let alive = true;
    documentsApi.movePreview(doc.id)
      .then(r => {
        if (!alive) return;
        const list = Array.isArray(r.data?.suggestions) ? r.data.suggestions : [];
        setDocBlockers(Array.isArray(r.data?.blockers) ? r.data.blockers : []);
        setNotices(r.data?.notices || null);
        setSuggestions(list);
        if (r.data?.plan) setPlan(r.data.plan);
        setSelected(sel => (sel == null && list.length === 1 ? list[0].restaurant_id : sel));
      })
      .catch(() => { if (alive) setSuggestions(s => s || []); })
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [doc.id]);

  // One preview per selection (the first answer already carries the confident
  // one's); a late answer for an earlier pick is dropped.
  const planFor = plan?.target?.id;
  useEffect(() => {
    if (!loaded || selected == null || String(planFor) === String(selected)) return;
    const seq = ++planSeq.current;
    documentsApi.movePreview(doc.id, selected)
      .then(r => { if (seq === planSeq.current) setPlan(r.data?.plan || null); })
      .catch(err => {
        if (seq !== planSeq.current) return;
        setPlan({ target: { id: selected }, blockers: [{ code: 'error', message: err.response?.data?.error || 'שגיאה בבדיקת המעבר' }] });
      });
  }, [doc.id, selected, loaded, planFor]);

  const suggestedIds = useMemo(() => new Set((suggestions || []).map(s => String(s.restaurant_id))), [suggestions]);
  // `hits` before the exclusions, so a search that found only this restaurant
  // or the suggestions already listed doesn't say nothing was found.
  const { results, hits } = useMemo(() => {
    const q = query.trim();
    if (q.length < MIN_QUERY) return { results: [], hits: 0 };
    const found = restaurants.filter(r => r.name && r.name.includes(q));
    return {
      results: found.filter(r => String(r.id) !== String(doc.restaurant_id) && !suggestedIds.has(String(r.id))),
      hits: found.length,
    };
  }, [restaurants, query, doc.restaurant_id, suggestedIds]);

  // Only the preview OF the chosen restaurant counts: for the render between a
  // new pick and its answer, the previous one is still in state.
  const current = plan && selected != null && String(plan.target?.id) === String(selected) ? plan : null;
  const blockers = current?.blockers || [];
  // A decided document moves only after the warnings have been shown: with the
  // first answer lost, the confirm waits rather than move it without them.
  const ready = current != null && blockers.length === 0 && !busy && (doc.status === 'טיוטה' || notices != null);
  const targetName = current?.target?.name
    || (suggestions || []).find(s => String(s.restaurant_id) === String(selected))?.name
    || restaurants.find(r => String(r.id) === String(selected))?.name || '';

  const isDraft = doc.status === 'טיוטה';
  const noun = isDraft ? 'הטיוטה' : 'המסמך';
  const submit = async () => {
    if (!ready) return;
    setBusy(true);
    try {
      const res = await documentsApi.move(doc.id, {
        target_restaurant_id: selected,
        ...(review ? { review: review() } : {}),
      });
      const moved = res.data?.moved || {};
      const picked = (suggestions || []).find(s => String(s.restaurant_id) === String(selected));
      track(EVENTS.DOCUMENT_MOVED, {
        matched_by: picked?.matched_by ?? null,
        suggested: !!moved.suggested,
        supplier_matched: !!moved.supplier_matched_by,
      });
      const to = joined(moved.target_name || targetName || 'מסעדה אחרת');
      const notice = isDraft ? `הטיוטה הועברה ל${to} · ממתינה שם בטיוטות` : `המסמך הועבר ל${to} · חזר לטיוטה וממתין שם לאישור${repricedLine(moved)}`;
      onMoved?.({ notice, doneLead: `הטיוטה הועברה ל${to}`, moved });
    } catch (err) {
      const data = err.response?.data;
      // A blocker that appeared since the preview: show it where the line is.
      if (err.response?.status === 409 && Array.isArray(data?.blockers)) setPlan(p => ({ ...(p || {}), target: { ...(p?.target || {}), id: selected }, blockers: data.blockers }));
      else toast(data?.error || `שגיאה בהעברת ${noun}`, 'error');
      setBusy(false);
    }
  };

  const title = 'העברה למסעדה אחרת';
  const docName = `${doc.doc_type}${doc.doc_num ? ' · ' + doc.doc_num : ''}`;

  // Nowhere to go: the reason, and a way out — no list to try in vain.
  if (docBlockers.length) {
    return (
      <ConfirmModal title={title} message={`אי אפשר להעביר את ${noun} "${docName}".`} cancelLabel="סגור" onCancel={onCancel}>
        {docBlockers.map(b => <div key={b.code} className="move-option-note is-blocked is-alone" role="alert">{b.message}</div>)}
      </ConfirmModal>
    );
  }

  // Nothing while the check runs: the confirm waits, the layout stays put.
  const optionLine = () => {
    if (!current) return null;
    if (blockers.length) return <div className="move-option-note is-blocked" role="alert">{blockers[0].message}</div>;
    if (!current.supplier) {
      const where = joined(current.target?.name || targetName);
      return (
        <div className="move-option-note">
          {current.supplier_hint ? <>הספק "<bdi>{current.supplier_hint}</bdi>" לא קיים ב<bdi>{where}</bdi></> : <>לא נמצא ספק תואם ב<bdi>{where}</bdi></>} — {isDraft ? 'הטיוטה תגיע' : 'המסמך יגיע'} בלי ספק
        </div>
      );
    }
    return null;
  };

  const option = (r) => {
    const picked = String(selected) === String(r.id);
    return (
      <div key={r.id}>
        <button
          type="button"
          role="radio"
          aria-checked={picked}
          className={`tenant-picker-option${picked ? ' active' : ''}`}
          onClick={() => setSelected(r.id)}
        >
          <span className="tenant-picker-check" aria-hidden="true">{picked && <Icon name="check" size={14} strokeWidth={2} />}</span>
          <bdi>{r.name}</bdi>
        </button>
        {picked && optionLine()}
      </div>
    );
  };

  const evidence = [
    doc.extracted_recipient_name && <>הנמען במסמך: <bdi>{doc.extracted_recipient_name}</bdi></>,
    doc.extracted_recipient_business_id && <>ח.פ. {doc.extracted_recipient_business_id}</>,
  ].filter(Boolean);
  const leftBehind = [unsavedEdits && 'שינויים שלא נשמרו לא יעברו', pendingNote && 'ההערה שממתינה ללקוח לא תישלח'].filter(Boolean);
  const commentCount = notices?.comments_left_behind || 0;
  const priced = notices?.notes_priced || { count: 0 };
  const toCheck = notices?.notes_to_check || { count: 0 };
  const calloutLines = [
    // Said even when the preview failed: the move can still go ahead.
    (notices?.returns_to_draft ?? !isDraft) && 'המסמך יחזור לטיוטה וימתין לאישור במסעדה החדשה',
    notices?.forward_sent && <>העותק שכבר נשלח להנה"ח (<bdi>{notices.forward_sent.sent_to}</bdi>) לא יימחק — יש למחוק אותו שם ידנית</>,
    priced.count === 1 && <>תעודת משלוח אחת{numbered(priced)} קיבלה מחיר מהחשבונית הזו — תתומחר מחדש ממקור אחר, ואם אין, תישאר במחיר הישן כמשוער</>,
    priced.count > 1 && <>{priced.count} תעודות משלוח{numbered(priced)} קיבלו מחיר מהחשבונית הזו — יתומחרו מחדש ממקור אחר, ואם אין, יישארו במחיר הישן כמשוער</>,
    toCheck.count === 1 && <>תעודת משלוח אחת{numbered(toCheck)} קיבלה מחיר מהחשבונית הזו וכבר שולמה, קושרה, אושרה או נשלחה — יש לבדוק אותה ידנית</>,
    toCheck.count > 1 && <>{toCheck.count} תעודות משלוח{numbered(toCheck)} קיבלו מחיר מהחשבונית הזו וכבר שולמו, קושרו, אושרו או נשלחו — יש לבדוק אותן ידנית</>,
    notices?.catalog_stays && 'מחירים ומוצרים שכבר נרשמו בקטלוג של המסעדה הנוכחית יישארו בו',
    commentCount === 1 && 'הערה אחת לא תעבור למסעדה החדשה',
    commentCount > 1 && `${commentCount} הערות לא יעברו למסעדה החדשה`,
    leftBehind.length > 0 && leftBehind.join(' · '),
  ].filter(Boolean);
  const several = (suggestions || []).length > 1;

  return (
    <ConfirmModal
      title={title}
      message={`לאיזו מסעדה להעביר את ${noun} "${docName}"?`}
      confirmLabel={busy ? 'מעביר…' : (isDraft ? 'העבר והבא' : 'העבר')}
      tone="primary"
      confirmDisabled={!ready}
      onConfirm={submit}
      onCancel={onCancel}
    >
      {evidence.length > 0 && (
        <div className="move-evidence">
          {evidence.map((e, i) => <span key={i}>{i > 0 && ' · '}{e}</span>)}
        </div>
      )}
      <div className="move-picker" role="radiogroup" aria-label="מסעדת היעד">
        {suggestions === null && <div className="tenant-picker-empty">טוען הצעות…</div>}
        {suggestions?.length > 0 && (
          <div className="move-group">
            <div className="move-group-title">
              {several ? 'כמה סניפים תואמים — יש לבחור סניף' : GROUP_TITLE[suggestions[0].matched_by] || GROUP_TITLE.name}
            </div>
            {suggestions.map(s => option({ id: s.restaurant_id, name: s.name }))}
          </div>
        )}
        <input
          className="tenant-picker-input"
          placeholder={`חפש מסעדה (${MIN_QUERY}+ תווים)`}
          aria-label="חיפוש מסעדה"
          value={query}
          onChange={e => setQuery(e.target.value)}
        />
        {query.trim().length >= MIN_QUERY && (
          <div className="tenant-picker-list">
            {results.length === 0
              ? <div className="tenant-picker-empty">{hits > 0 ? 'אין עוד מסעדות בשם הזה' : 'לא נמצאו תוצאות'}</div>
              : results.map(option)}
          </div>
        )}
        {/* A restaurant picked by search and then searched away stays chosen —
            its line keeps showing below the list. */}
        {selected != null && !suggestedIds.has(String(selected)) && !results.some(r => String(r.id) === String(selected)) && (
          <div className="move-group" style={{ marginTop: 10 }}>
            {option({ id: selected, name: targetName })}
          </div>
        )}
      </div>
      {/* What stays behind, read right before the confirm. */}
      {calloutLines.length > 0 && (
        <AmberCallout>
          <div className="move-callout-lines">{calloutLines.map((line, i) => <div key={i}>{line}</div>)}</div>
        </AmberCallout>
      )}
    </ConfirmModal>
  );
}
